'use client';

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export type ButtonVariant = 'default' | 'accent' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const BASE_BUTTON_CLASSES =
  'inline-flex items-center justify-center gap-2 rounded-full border font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/* Pill buttons: accent is the solid cobalt CTA; default is the quiet
   white/outline secondary. */
const variantStyles: Record<ButtonVariant, string> = {
  default:
    'bg-secondary text-text-primary border-secondary hover:bg-surface-hover hover:border-neutral focus-visible:ring-accent',
  accent:
    'bg-accent text-accent-foreground border-transparent hover:bg-accent-hover focus-visible:ring-accent',
  ghost:
    'bg-transparent text-text-primary border-transparent hover:bg-surface-hover focus-visible:ring-accent',
  danger:
    'bg-danger-fill text-danger-foreground border-transparent hover:bg-danger-fill-hover focus-visible:ring-danger',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
  icon: 'h-9 w-9 p-0',
};

interface ButtonClassNameOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

export const getButtonClassName = ({
  variant = 'default',
  size = 'md',
  className,
}: ButtonClassNameOptions = {}) =>
  cn(BASE_BUTTON_CLASSES, variantStyles[variant], sizeStyles[size], className);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** When true the button shows a loading state and suppresses clicks */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      type = 'button',
      loading = false,
      disabled,
      ...props
    },
    ref
  ) => {
    const finalDisabled = Boolean(disabled || loading);

    return (
      <button
        ref={ref}
        type={type}
        // Keep a concise base class 'btn' for compatibility with tests and tooling
        className={`btn ${getButtonClassName({ variant, size, className })}`}
        data-variant={variant}
        aria-busy={loading || undefined}
        disabled={finalDisabled}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

export default Button;
